'use client';

import React, { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { BenzingaEdgeProvider, EdgeCheckoutHost } from '@benzinga/edge';
import { CustomError } from '@benzinga/templates';

import RegisterEffects from '../root/RegisterEffects';
import RootScripts from '../root/RootScripts';
import RootTracker from '../root/RootTrackers';
import { PageStructuredData } from '../root/PageStructuredData';
import { resolvePageConfig } from '../../_libs/site-shell/resolvePageConfig';
import { RouteMetaProvider } from './RouteMetaContext';
import PageTracking from './PageTracking';
import type { SitePageConfig } from './pageConfig';

export type { SitePageConfig } from './pageConfig';

interface SitePageProvidersProps {
  children: React.ReactNode;
  pageConfig?: SitePageConfig;
}

function mergePageConfig(pathnameConfig: SitePageConfig, pageConfig: SitePageConfig): SitePageConfig {
  return {
    ...pathnameConfig,
    ...pageConfig,
    pageTargeting: pathnameConfig.pageTargeting ?? pageConfig.pageTargeting,
    disableRaptiveFooter: pathnameConfig.disableRaptiveFooter ?? pageConfig.disableRaptiveFooter,
    disableRaptiveReadyOnPageLoad:
      pathnameConfig.disableRaptiveReadyOnPageLoad ?? pageConfig.disableRaptiveReadyOnPageLoad,
  };
}

export default function SitePageProviders({ children, pageConfig = {} }: SitePageProvidersProps) {
  const pathname = usePathname();
  const pathnamePageConfig = useMemo(() => resolvePageConfig(pathname), [pathname]);
  const mergedPageConfig = useMemo(
    () => mergePageConfig(pathnamePageConfig, pageConfig),
    [pathnamePageConfig, pageConfig],
  );

  return (
    <RouteMetaProvider>
      {mergedPageConfig.metaProps ? <PageStructuredData metaProps={mergedPageConfig.metaProps} /> : null}
      <RegisterEffects />
      <RootTracker pageProps={mergedPageConfig} />
      <RootScripts pageProps={mergedPageConfig} />
      <BenzingaEdgeProvider
        disableRaptiveFooter={mergedPageConfig.disableRaptiveFooter ?? false}
        disableRaptiveReadyOnPageLoad={mergedPageConfig.disableRaptiveReadyOnPageLoad}
        initialQueryParams={pageConfig.queryParams as Record<string, string> | undefined}
        initialTargeting={mergedPageConfig.pageTargeting}
      >
        <EdgeCheckoutHost />
        {mergedPageConfig.error ? (
          <CustomError statusCode={mergedPageConfig.error} />
        ) : (
          <>
            {children}
            <PageTracking pageConfig={mergedPageConfig} />
          </>
        )}
      </BenzingaEdgeProvider>
    </RouteMetaProvider>
  );
}
